// Behaviour artifacts as per-trace feed citizens.
//
// The feed build is pure and synchronous; artifact execution is neither — it
// runs Python in the isolated realm. This module is the seam between them: the
// caller asks once per trace, execution happens off the render path, results
// cache per (trace, artifact version), and the feed re-builds with the ops
// merged through the same block-id door as every extractor. The cache key
// includes the version so an edited artifact re-runs and a stale build can
// never claim under a new build's name; a re-render never re-executes.
//
// Failure classes stay separate here too: an artifact that THREW (or a realm
// that is unavailable) contributes nothing and is reported on the result — its
// work stays residual, which is the honest reading. It is never retried in a
// loop: the refusal caches like a result, keyed to the same version, so one
// broken artifact costs one realm call per trace, not one per render.

import { listBehaviourArtifacts, artifactVersion } from "./behaviourArtifactStore.js";
import { runArtifactOverBodies } from "./xrayArtifactRun.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";

// Per-trace budget, deliberately tighter than the Behaviours tab's window runs:
// the feed is a reading surface, and a lens that needs more than this per trace
// belongs in the batch tier, not the read path. ONE deadline for the whole
// trace, split across every mounted artifact — N artifacts share it rather
// than each collecting the full allowance, so opening a trace can never stall
// N× the stated budget. An artifact the budget never reaches is skipped with
// its own problem line (and not cached, so a later open retries it).
const TRACE_TIMEOUT_MS = 8_000;
const CACHE_CAP = 400;

const cache = new Map();
const inflight = new Map();
onAuthIdentityChange(() => {
  cache.clear();
  inflight.clear();
});

function remember(key, value) {
  cache.set(key, value);
  if (cache.size > CACHE_CAP) {
    // Maps iterate in insertion order, so the first key is the oldest entry.
    cache.delete(cache.keys().next().value);
  }
  return value;
}

/** An occurrence, reshaped as a feed op the generic row logic can render. */
function opOfOccurrence(occ, manifest) {
  return {
    key: occ.key,
    name: manifest?.name || occ.behaviour,
    outcome: occ.call,
    fields: Object.entries(occ.values || {}).map(([name, value]) => ({ name, value: String(value) })),
    checks: Object.entries(occ.claims || {}).map(([name, ok]) => ({ name, ok: Boolean(ok) })),
  };
}

/**
 * Every mounted artifact's ops for ONE trace, cached by (trace, version).
 *
 * @param {object} trace  { traceId, sessionId, endpointId, ts, turns }
 * @returns {Promise<{groups: {domain, ops}[], problems: string[]}>}
 *   `groups` feeds buildSemanticFeed's `extraOps`; `problems` names artifacts
 *   that threw or emitted invalid rows, for the caller to surface or log.
 */
export async function artifactOpsForTrace(trace, { signal } = {}) {
  const epoch = getAuthIdentityEpoch();
  const artifacts = listBehaviourArtifacts();
  if (!artifacts.length || !Array.isArray(trace?.turns) || !trace.turns.length) {
    return { groups: [], problems: [] };
  }
  const body = {
    promptId: trace.traceId || "trace",
    sessionId: trace.sessionId || "",
    endpointId: trace.endpointId || null,
    ts: trace.ts || null,
    parsed: { turns: trace.turns },
  };

  const groups = [];
  const problems = [];
  const deadline = Date.now() + TRACE_TIMEOUT_MS;
  for (const artifact of artifacts) {
    // Behaviour id AND content version: the version alone is a hash of source
    // + manifest, which two artifacts can legitimately share (a copied lens
    // renamed under a new id), and a shared key would hand one artifact the
    // other's cached reading.
    const key = `${epoch}::${body.promptId}::${artifact.behaviour}@${artifactVersion(artifact)}`;
    let entry = cache.get(key);
    if (!entry) {
      let pending = inflight.get(key);
      if (!pending) {
        // Cache hits and other artifacts' cached results cost nothing; only
        // live runs draw down the trace's one deadline.
        const remainingMs = deadline - Date.now();
        if (remainingMs <= 0) {
          problems.push(`${artifact.behaviour}: skipped — the trace's execution budget was spent before its turn; its work stays residual until the next open`);
          continue;
        }
        pending = runArtifactOverBodies(artifact, [body], { signal, timeoutMs: remainingMs })
          .then((r) => getAuthIdentityEpoch() === epoch ? remember(key, r) : ({ ok: false, occurrences: [], invalid: [] }))
          .catch((err) => getAuthIdentityEpoch() === epoch ? remember(key, {
            ok: false,
            threw: err?.message || String(err),
            occurrences: [],
            invalid: [],
          }) : ({ ok: false, occurrences: [], invalid: [] }))
          .finally(() => inflight.delete(key));
        inflight.set(key, pending);
      }
      entry = await pending;
    }
    if (entry.threw) {
      problems.push(`${artifact.behaviour}: threw — ${String(entry.threw).slice(0, 200)}`);
      continue;
    }
    if (entry.invalid?.length) {
      problems.push(`${artifact.behaviour}: ${entry.invalid.length} emitted row${entry.invalid.length === 1 ? "" : "s"} refused by the contract`);
    }
    if (entry.occurrences?.length) {
      groups.push({
        domain: artifact.behaviour,
        ops: entry.occurrences.map((occ) => opOfOccurrence(occ, artifact.manifest)),
      });
    }
  }
  return { groups, problems };
}

/** Test-only: drop everything. */
export function _resetArtifactTraceFeedForTests() {
  cache.clear();
  inflight.clear();
}
